import React, { useRef, useEffect, useCallback } from 'react';
import { Textarea } from '../ui/textarea';

/**
 * Textarea that grows with its content. Drop-in replacement for
 * `<Textarea>` — removes the scroll-to-see-more annoyance.
 *
 * Props: same as Textarea, plus `minRows` (default 2).
 */
export default function AutoTextarea({
  value,
  onChange,
  minRows = 2,
  className = '',
  style = {},
  ...rest
}) {
  const ref = useRef(null);

  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // Reset first so shrinking works when the user deletes text
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  // Re-fit when value changes (incl. programmatic updates e.g. translation)
  useEffect(() => { fit(); }, [value, fit]);
  // Also re-fit on window resize (wrap width can change)
  useEffect(() => {
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [fit]);

  const handleChange = (e) => {
    if (onChange) onChange(e);
    // Let React state update first, then size. Doing it now also works
    // for plain inputs, but a second fit ensures wrapped lines count.
    fit();
  };

  return (
    <Textarea
      ref={ref}
      value={value}
      onChange={handleChange}
      rows={minRows}
      className={`resize-none overflow-hidden ${className}`}
      style={{ minHeight: `${minRows * 22}px`, ...style }}
      {...rest}
    />
  );
}
